import { useCallback, useMemo } from 'react'
import { useSelector } from 'react-redux'
import { useNavigate } from 'react-router'
import { SetURLSearchParams } from 'react-router-dom'

import { useGetCMSMicrositeMessageHandlers as useGetCMSMicrositeMessageHandlersShared } from '@lyrahealth-inc/shared-app-logic/src/features/CMSMicrosite/hooks/useGetCMSMicrositeMessageHandlers'
import { CMS_MICROSITE_MESSAGE } from '@lyrahealth-inc/shared-app-logic/src/features/CMSMicrosite/messages/messages'
import { CMS_MICROSITE_ID } from '@lyrahealth-inc/shared-app-logic/src/features/CMSMicrosite/types'
import { getMicrositePathwayKey } from '@lyrahealth-inc/shared-app-logic/src/features/CMSMicrosite/utils'
import { RESOURCE_COORDINATOR_SCHEDULE_CALL_LINK } from '@lyrahealth-inc/shared-app-logic/src/features/common/constants/constants'
import {
  COMMON_MIXPANEL_PROPERTIES,
  INTEGRATED_BENEFITS_EVENTS,
} from '@lyrahealth-inc/shared-app-logic/src/features/common/constants/mixpanelConstants'
import {
  PAGE_ROUTES,
  WELLNESS_TOOLKIT_BASE_ROUTE,
} from '@lyrahealth-inc/shared-app-logic/src/features/common/constants/routingConstants'
import {
  ESSENTIALS_CONTENT_CATEGORY,
  EssentialsContentData,
} from '@lyrahealth-inc/shared-app-logic/src/features/essentials/types'
import { getEssentialsSubpath } from '@lyrahealth-inc/shared-app-logic/src/features/essentials/utils'
import { PathwayOptions } from '@lyrahealth-inc/shared-app-logic/src/features/pathways/constants'
import { ProviderProgramTaxonomy } from '@lyrahealth-inc/shared-app-logic/src/models/providers/Providers'

import { getScheduleResourceCoordinatorCallLink } from '../../../data/customer/customerSelectors'
import { trackEventWithObj } from '../../../data/mixpanel'
import { useAppDispatch } from '../../../data/storeConfiguration/store'
import { getIsUserLoggedIn } from '../../../data/user/userSelectors'
import { setSelectedPathwayOption, setSelectedPathwaySubpath } from '../../../features/register/data/registerActions'
import { useOpenUrl } from '../../../hooks/useOpenUrl'
import { useGetSearchForCareEventProps } from '../useGetSearchForCareEventProps'
import {
  useNavigateToEssentialsContent,
  useNavigateToEssentialsContentWebProps,
} from '../useNavigateToEssentialsContent'
import { useNavigateToWellnessCheckIn } from '../useNavigateToWellnessCheckIn'

type navigateToEssentialsHomepageWidgetParams = {
  slug?: string
  category?: ESSENTIALS_CONTENT_CATEGORY
  path?: string
}

/**
 * Get post message event handlers based on the cms microsite's metadata
 */
export const useGetCMSMicrositeMessageHandlers = ({
  eventsList,
  page,
  searchParams,
  setSearchParams,
  setIsShowingRegistrationModal,
  micrositeId,
  openCareNavigatorModal,
  utmCampaign,
}: {
  eventsList: { key: CMS_MICROSITE_MESSAGE; params?: Dict }[]
  page: string
  searchParams: URLSearchParams
  setSearchParams: SetURLSearchParams
  setIsShowingRegistrationModal: React.Dispatch<React.SetStateAction<boolean>>
  micrositeId: CMS_MICROSITE_ID
  openCareNavigatorModal: () => void
  utmCampaign?: string
}): { [key in CMS_MICROSITE_MESSAGE]?: () => void } => {
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const isLoggedIn = useSelector(getIsUserLoggedIn)
  const openUrl = useOpenUrl()
  const navigateToEssentialsContent = useNavigateToEssentialsContent()
  const { navigateToWellnessCheckInForm } = useNavigateToWellnessCheckIn()
  const getAdditionalMixpanelProps = useGetSearchForCareEventProps()
  const scheduleResourceCoordinatorCallLink = useSelector(getScheduleResourceCoordinatorCallLink)

  const registrationEntryPointState = useMemo(() => {
    return {
      state: { registrationEntryPoint: page },
    }
  }, [page])

  const navigateToCareOptions = useCallback(() => {
    navigate(PAGE_ROUTES.PATHWAYS.FORKED_TRIAGE)
  }, [navigate])

  const navigateToCriticalIncidentSupportToolkit = useCallback(() => {
    navigate(WELLNESS_TOOLKIT_BASE_ROUTE + '/trauma')
  }, [navigate])

  const navigateToWellnessCheckIn = useCallback(() => {
    navigateToWellnessCheckInForm({ entryPoint: page })
  }, [navigateToWellnessCheckInForm, page])

  const navigateToEssentials = useCallback(
    ({ navigateToEssentialsHookProps }: { navigateToEssentialsHookProps: useNavigateToEssentialsContentWebProps }) => {
      navigateToEssentialsContent(navigateToEssentialsHookProps)
    },
    [navigateToEssentialsContent],
  )

  const navigateToEssentialsHomepageWidget = useCallback(
    ({ slug, category, path }: navigateToEssentialsHomepageWidgetParams) => {
      const subPathStr = getEssentialsSubpath({ slug, category, subPath: path })
      navigate(PAGE_ROUTES.PATHWAYS.ESSENTIALS_HOMEPAGE_WIDGET, registrationEntryPointState)
      dispatch(setSelectedPathwayOption(PathwayOptions.ESSENTIALS))
      dispatch(setSelectedPathwaySubpath(subPathStr))
    },
    [dispatch, navigate, registrationEntryPointState],
  )

  const navigateToWebinar = useCallback(({ webinarLink }: { webinarLink: string }) => {
    // Essentials is served by lyraweb-mobile via the webpack/nginx proxy. SPA navigation
    // bypasses the proxy and falls through to the secureRoutes catch-all, so use a full
    // document navigation to hand off to the mobile app.
    window.location.assign(webinarLink)
  }, [])

  const showRegistrationModal = useCallback(
    (messageKey: CMS_MICROSITE_MESSAGE) => {
      // Since modal opens on the toolkit, 'page' defaults to the toolkit but 'entryPoint' needs to be set
      const newParams = new URLSearchParams(searchParams)
      newParams.set(COMMON_MIXPANEL_PROPERTIES.ENTRY_POINT, page)
      setSearchParams(newParams)

      setIsShowingRegistrationModal(true)
      dispatch(setSelectedPathwayOption(getMicrositePathwayKey(micrositeId, messageKey)))
    },
    [dispatch, micrositeId, page, searchParams, setIsShowingRegistrationModal, setSearchParams],
  )

  const navigateToRegistration = useCallback(() => {
    navigate('/register/connect', registrationEntryPointState)
  }, [navigate, registrationEntryPointState])

  const clickSelfCareContent = useCallback(
    ({ contentData, contentSlug }: { contentData?: EssentialsContentData; contentSlug?: string }) => {
      const slug = contentData?.slug || contentSlug
      if (slug) {
        navigateToEssentialsContent({ slug, newWindow: true, loggedIn: isLoggedIn })
      }
    },
    [navigateToEssentialsContent, isLoggedIn],
  )

  const navigateToProviderBooking = useCallback(
    ({ providerId, ctpo, newTab }: { providerId: string; ctpo?: ProviderProgramTaxonomy; newTab?: boolean }) => {
      if (newTab) {
        const url = new URL(`${window.location.origin}/secure/index/provider/${providerId}`)
        if (utmCampaign) {
          url.searchParams.set('utm_campaign', utmCampaign)
        }
        if (ctpo) {
          Object.entries(ctpo).forEach(([key, value]) => {
            url.searchParams.set(key, String(value))
          })
        }
        window.open(url.toString(), '_blank', 'noopener')
      } else {
        navigate(PAGE_ROUTES.PROVIDER_PROFILE(providerId), { state: { ctpo } })
      }
    },
    [navigate, utmCampaign],
  )

  const handleScheduleResourceCoordinatorCall = useCallback(
    ({ message }: { message: MessageEvent }) => {
      const locationOnPage = message.data?.mixPanelData?.entrypoint ?? ''
      dispatch(
        trackEventWithObj({
          event: INTEGRATED_BENEFITS_EVENTS.CLICK_SCHEDULE_RESOURCE_COORDINATOR_CALL,
          ...getAdditionalMixpanelProps({}, page),
          [COMMON_MIXPANEL_PROPERTIES.ENTRY_POINT]: locationOnPage,
        }),
      )
      openUrl(scheduleResourceCoordinatorCallLink ?? RESOURCE_COORDINATOR_SCHEDULE_CALL_LINK)
    },
    [dispatch, getAdditionalMixpanelProps, openUrl, page, scheduleResourceCoordinatorCallLink],
  )

  return useGetCMSMicrositeMessageHandlersShared({
    eventsList,
    isLoggedIn,
    navigateToCareOptions,
    navigateToWellnessCheckIn,
    navigateToCriticalIncidentSupportToolkit,
    navigateToEssentials,
    navigateToEssentialsHomepageWidget,
    navigateToWebinar,
    navigateToRegistration,
    showRegistrationModal,
    clickSelfCareContent,
    openExternalLink: ({ link }) => openUrl(link),
    navigateToProviderBooking,
    openCareNavigatorModal,
    scheduleResourceCoordinatorCall: handleScheduleResourceCoordinatorCall,
  })
}
